'use client';

import React, { useState, forwardRef } from 'react';
import classnames from 'classnames';

import Box from '../Box/Box';
import Typography from '../Typography/Typography';
import Iconography from '../Iconography/Iconography';
import Row from '../Grid/Row/Row';

import styles from './Input.module.scss';

type InputType = 'text' | 'password' | 'credit-card' | 'currency' | 'file';
type InputSize = 'small' | 'medium' | 'large';
type Theme = 'dark' | 'light';
type Direction = 'ltr' | 'rtl';

interface ValidationResponse {
  validationType?: 'success' | 'warning' | 'error';
  message?: string;
}

interface IconProps {
  category: string;
  name: string;
  dataTestId?: string;
}

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  name: string;
  register?: (name: string) => Record<string, unknown>;
  theme?: Theme;
  type?: InputType;
  size?: InputSize;
  label?: string;
  helperText?: string;
  placeholder?: string;
  className?: string;
  direction?: Direction;
  unitText?: string;
  helperIcon?: IconProps;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  dataTestId?: string;
  /**
   * Validation response object containing error/warning/success state and message.
   */
  validationResponse?: ValidationResponse;
  /**
   * Whether to show the validation message area.
   * @default true
   */
  showValidation?: boolean;
  required?: boolean;
  disabled?: boolean;
  handleIconClick?: () => void;
  controlled?: boolean;
  allowMultipleFiles?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      name,
      register,
      theme = 'dark',
      type = 'text',
      size = 'medium',
      label,
      helperText,
      placeholder,
      className = '',
      direction = 'ltr',
      unitText,
      helperIcon = {},
      inputProps,
      dataTestId,
      validationResponse,
      showValidation = true,
      required = false,
      handleIconClick,
      disabled = false,
      controlled = true,
      allowMultipleFiles = false,
      onBlur,
      onChange,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [value, setValue] = useState('');
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const isPassword = type === 'password';
    const isCreditCard = type === 'credit-card';
    const isCurrency = type === 'currency';
    const defaultCCPlaceholder = '9999 9999 9999 9999';

    const AUTOCOMPLETE_TYPES: Record<string, string> = {
      firstName: 'given-name',
      lastName: 'family-name',
      email: 'email',
      zipCode: 'postal-code',
    };

    const iconMap: Record<string, IconProps | undefined> = {
      password: {
        category: 'global',
        name: showPassword ? 'hide' : 'show',
      },
      'credit-card': {
        category: 'global',
        name: 'credit-card',
      },
      text: 'category' in helperIcon ? (helperIcon as IconProps) : undefined,
    };

    const handlePasswordToggle = () => setShowPassword((prev) => !prev);

    const formatCreditCard = (value: string) =>
      value
        .replace(/\D/g, '')
        .slice(0, 16)
        .replace(/(\d{4})(?=\d)/g, '$1 ');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let inputValue = e.target.value;
      if (type === 'credit-card') {
        // Remove all non-digit characters and format with spaces
        inputValue = formatCreditCard(inputValue);
      }

      if (type === 'file') {
        // Handle file selection
        const files = Array.from(e.target.files || []);
        setSelectedFiles(files);
        // Call external onChange with files
        if (onChange) {
          onChange(e);
        }
      } else {
        setValue(inputValue);
        // Call external onChange with the processed value
        if (onChange) {
          // Create a synthetic event with the processed value for form compatibility
          const syntheticEvent = {
            ...e,
            target: { ...e.target, value: inputValue },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }
      }
    };

    const handleRemoveFile = (fileIndex: number) => {
      setSelectedFiles((prevFiles) => prevFiles.filter((_, index) => index !== fileIndex));
    };

    // Get registration object to access onBlur for react-hook-form validation
    const registration = register ? register(name) : null;

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // Auto-trim whitespace for text-based inputs (not password, file, or currency)
      if (type === 'text' || type === 'credit-card') {
        const trimmedValue = e.target.value.trim();
        if (trimmedValue !== e.target.value) {
          setValue(trimmedValue);
          if (onChange) {
            const syntheticEvent = {
              ...e,
              target: { ...e.target, value: trimmedValue },
            } as React.ChangeEvent<HTMLInputElement>;
            onChange(syntheticEvent);
          }
        }
      }
      // Call react-hook-form's onBlur for validation
      if (registration?.onBlur) {
        (registration.onBlur as (e: React.FocusEvent<HTMLInputElement>) => void)(e);
      }
      // Call original onBlur if provided
      if (onBlur) {
        onBlur(e);
      }
    };

    const renderIcon = () => {
      if (!unitText && !helperIcon?.name && !iconMap[type]?.name) {
        return null; // No icons or unit text, skip rendering
      }

      return (
        <Row align="middle" gutter={[0, 4]} className={classnames(styles.endIcons, styles[size])}>
          {unitText && type === 'text' && (
            <Box>
              <Typography
                variant="body"
                size="medium"
                color="gray-3"
                theme={theme}
                direction={direction}
              >
                {unitText}
              </Typography>
            </Box>
          )}
          {iconMap[type]?.name && (
            <Box>
              <button
                type="button"
                onClick={isPassword ? handlePasswordToggle : handleIconClick}
                disabled={disabled}
                className={styles.helperIcon}
                aria-label={
                  isPassword ? (showPassword ? 'Hide password' : 'Show password') : 'Helper icon'
                }
              >
                <span className={styles.helperIcon} data-testid={iconMap[type].dataTestId}>
                  <Iconography
                    category={iconMap[type].category as 'global' | 'brand'}
                    name={iconMap[type].name}
                    size={20}
                    color={theme === 'dark' ? 'white' : 'black'}
                  />
                </span>
              </button>
            </Box>
          )}
        </Row>
      );
    };

    const resolvedValue = props.value ?? value;

    return (
      <Box
        className={classnames(
          styles.input,
          className,
          styles[`theme-${theme}`],
          styles[`validation-${validationResponse?.validationType}`],
          disabled && styles.disabled
        )}
        aria-disabled={disabled}
        {...props}
      >
        {(label || helperText) && (
          <Box mb={4}>
            {label && (
              <label htmlFor={name}>
                <Typography
                  className={styles.label}
                  variant="label"
                  size="small"
                  direction={direction}
                  theme={theme}
                  color="gray-3"
                >
                  {label}
                  {required && '*'}
                </Typography>
              </label>
            )}
            {helperText && (
              <Typography
                className={styles.helperText}
                variant="body"
                size="extraSmall"
                direction={direction}
                theme={theme}
                color="gray-5"
              >
                {helperText}
              </Typography>
            )}
          </Box>
        )}
        <Row
          gutter={[0, 0]}
          justify="space-between"
          align="middle"
          className={classnames(styles.inputRow, {
            [styles.withValidation]: showValidation || type === 'file',
          })}
        >
          <input
            ref={ref}
            className={classnames(styles.inputField, styles[size], styles[direction], styles[type])}
            type={(isPassword && showPassword) || isCreditCard || isCurrency ? 'text' : type}
            placeholder={
              isCreditCard
                ? formatCreditCard(placeholder || '') || defaultCCPlaceholder
                : placeholder
            }
            {...(controlled && type !== 'file' ? { value: resolvedValue } : {})}
            required={required}
            data-testid={dataTestId}
            {...(isCurrency ? { 'data-currency-input': true } : {})}
            {...(inputProps ?? {})}
            id={name}
            name={name}
            {...(type === 'file' ? { multiple: allowMultipleFiles } : {})}
            aria-describedby={`${name}-validation-message`}
            autoComplete={AUTOCOMPLETE_TYPES[name] || 'off'}
            disabled={disabled}
            {...(registration ? { ...registration, onBlur: undefined, onChange: undefined } : {})}
            onChange={
              controlled
                ? handleChange
                : (e: React.ChangeEvent<HTMLInputElement>) => {
                    // For uncontrolled inputs, call react-hook-form's onChange for validation
                    if (registration?.onChange) {
                      (registration.onChange as (e: React.ChangeEvent<HTMLInputElement>) => void)(
                        e
                      );
                    }
                    // Also call the external onChange (e.g. postal code auto-populate)
                    if (onChange) {
                      onChange(e);
                    }
                  }
            }
            onBlur={handleBlur}
          />
          {renderIcon()}
        </Row>
        {type === 'file' && selectedFiles.length > 0 && (
          <Box mt={8} className={styles.selectedFiles}>
            <Row gutter={[8, 8]} wrap>
              {selectedFiles.map((file, index) => (
                <div key={`${file.name}-${index}`} className={styles.fileItem}>
                  <span>{file.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className={styles.removeFile}
                    aria-label={`Remove ${file.name}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </Row>
          </Box>
        )}
        {showValidation && (
          <Box className={styles.validationMessageWrapper} role="alert" aria-live="assertive">
            {validationResponse?.message && (
              <Typography
                className={classnames(styles.validationMessage)}
                variant="body"
                size="extraSmall"
                direction={direction}
                theme={theme}
                id={`${name}-validation-message`}
              >
                {validationResponse.message}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }
);

Input.displayName = 'Input';

export default Input;
